import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Heading,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  CollecticonChevronLeftSmall,
  CollecticonChevronRightSmall,
  CollecticonTickSmall,
  CollecticonXmarkSmall,
} from "@devseed-ui/collecticons-chakra";
import { Layer, LngLatBoundsLike, Source } from "react-map-gl";
import { Feature } from "geojson";
import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import mask from "@turf/mask";

import {
  Loading,
  Error,
  Map,
  MapScenePreview,
  Overline,
} from "../../components";
import MetaDataCard, {
  MetaData,
  MetaDataHeader,
} from "../../components/MetaDataCard";
import { formatDate, getLikelySpecies } from "../../utils";
import { useModel } from "../../context/AppConfig";
import Panel, { PanelBody } from "../../components/Panel";
import { ValidatedBadge } from "../../components/DetectionCard";
import { useSceneInferences, useScene } from "../../hooks";
import ImageControls from "../../components/ImageControls";
import { DEFAULT_BRIGHTNESS, DEFAULT_CONTRAST } from "../../settings";
import { Inference } from "../../types/inferences";
import { PANEL_SIZE } from "../../theme";

function Validate() {
  const { sceneId } = useParams();

  const {
    scene,
    error: sceneError,
    isPending: sceneIsPending,
  } = useScene(sceneId);
  const {
    inferences,
    error: inferencesError,
    isPending: inferencesIsPending,
    validate,
    isValidating,
    validationError,
  } = useSceneInferences(sceneId);

  const [currentInference, setCurrentInference] = useState<number>(0);
  const [brightness, setBrightness] = useState<number>(DEFAULT_BRIGHTNESS);
  const [contrast, setContrast] = useState<number>(DEFAULT_CONTRAST);
  const [bboxMask, setBboxMask] = useState<boolean>(true);
  const inferencesSorted = useMemo(() => inferences?.features.sort(
    (inferenceA: Inference, inferenceB: Inference) =>
      Number(getLikelySpecies(inferenceB).confidence) -
      Number(getLikelySpecies(inferenceA).confidence)
  ), [inferences]);
  const selectedInference = inferencesSorted?.[currentInference];
  const model = useModel(selectedInference?.properties.model_id);

  const bounds: LngLatBoundsLike | undefined = useMemo(() => {
    if (!selectedInference) return;
    const [x1, y1, x2, y2] = bbox(selectedInference as Feature);
    return [x1, y1, x2, y2];
  }, [selectedInference]);

  const borderColor = useColorModeValue("base.100a", "base.500a");

  if (sceneIsPending || inferencesIsPending) {
    return <Loading mt="8" />;
  }

  if (!sceneId) {
    return <Error>Not found</Error>;
  }

  if (sceneError || inferencesError) {
    return <Error>{sceneError?.message || inferencesError?.message}</Error>;
  }

  if (!inferences || inferences?.features.length === 0) {
    return <Error>No inferences left to validate in this scene</Error>;
  }

  if (!selectedInference) {
    return <Error>Unable to select inference</Error>;
  }

  const sceneDate = formatDate(new Date(Date.parse(scene.properties.date)));
  const { species, confidence } = getLikelySpecies(selectedInference);
  const isValidated =
    selectedInference.properties.is_valid !== undefined &&
    selectedInference.properties.is_valid !== null;

  return (
    <Box display="grid" gridTemplateRows="max-content 1fr">
      <Box
        display="flex"
        px="4"
        py="3"
        gap="4"
        alignItems="center"
        borderBottom="1px"
        borderColor={borderColor}
      >
        <Box>
          <Button
            size="sm"
            variant="base-soft-outline"
            as={Link}
            to={`/scenes/${sceneId}`}
          >
            Exit
          </Button>
        </Box>
        <Box
          mx="auto"
          display="flex"
          alignItems="baseline"
          fontSize="sm"
          gap={2}
        >
          <Overline>Scene</Overline>
          <Text fontWeight="semibold">{sceneDate}</Text>/{" "}
          <Overline>Detection</Overline>{" "}
          <Text fontWeight="semibold">
            {currentInference + 1} of {inferencesSorted.length}
          </Text>
        </Box>
        <Box display="flex" gap="2">
          <Button
            size="sm"
            variant="base-text"
            title="Navigate to the previous detection"
            onClick={() => setCurrentInference((i) => i - 1)}
            isDisabled={currentInference === 0 || isValidating}
            leftIcon={<CollecticonChevronLeftSmall />}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="base-text"
            title="Navigate to the next detection"
            onClick={() => setCurrentInference((i) => i + 1)}
            isDisabled={
              currentInference === inferencesSorted.length - 1 ||
              isValidating
            }
            rightIcon={<CollecticonChevronRightSmall />}
          >
            Next
          </Button>
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns={`1fr ${PANEL_SIZE}`} overflow="hidden">
        <Map bounds={bounds} boundsPadding={200} zoomDuration={2000} miniMap>
          <MapScenePreview
            id={sceneId}
            brightness={brightness}
            contrast={contrast}
          />
          {bboxMask && (
            <Source
              id="inference-bbbox-mask"
              type="geojson"
              data={mask(bboxPolygon(selectedInference.bbox!))} // eslint-disable-line @typescript-eslint/no-non-null-assertion
            >
              <Layer
                id="inference-bbbox-mask-layer"
                beforeId="inference-bbbox-layer"
                type="fill"
                paint={{
                  "fill-color": "rgba(0,0,0,0.3)",
                }}
              />
            </Source>
          )}
          <Source
            id="inference-bbbox-source"
            type="geojson"
            data={bboxPolygon(selectedInference.bbox!)} // eslint-disable-line @typescript-eslint/no-non-null-assertion
          >
            <Layer
              id="inference-bbbox-layer"
              type="line"
              paint={{
                "line-color": isValidated
                  ? selectedInference.properties.is_valid
                    ? "#48BB78"
                    : "#F56565"
                  : "#1ed0f2",
                "line-width": 2,
              }}
            />
          </Source>
        </Map>
        <Panel>
          <PanelBody>
            <Box
              display="flex"
              gap="4"
              pb="4"
              borderBottom="1px solid"
              borderColor={borderColor}
              alignItems="center"
            >
              <Heading size="md" as="h2" flex="1">
                Validate Detection
              </Heading>
              {isValidated && (
                <ValidatedBadge
                  isValid={selectedInference.properties.is_valid}
                />
              )}
            </Box>
            <MetaDataCard>
              <MetaDataHeader as="h3">Scene Metadata</MetaDataHeader>
              <MetaData
                label="Scene Id"
                value={`${scene.properties.s3_prefix.substring(0, 20)}...`}
              />
              <MetaData label="Aquired" value={sceneDate} />
            </MetaDataCard>
            <MetaDataCard>
              <MetaDataHeader as="h3">Detection Details</MetaDataHeader>
              <MetaData
                label="Model"
                value={`${model?.name} (${model?.version})`}
              />
              <MetaData label="ymin" value={selectedInference.properties.inference.pixel_coords.ymin} />
              <MetaData label="xmin" value={selectedInference.properties.inference.pixel_coords.xmin} />
              <MetaData label="ymax" value={selectedInference.properties.inference.pixel_coords.ymax} />
              <MetaData label="xmax" value={selectedInference.properties.inference.pixel_coords.xmax} />
              <MetaData label="Species" value={species} />
              <MetaData label="Confidence" value={confidence} valueAsTag />
            </MetaDataCard>
            <ButtonGroup width="100%">
              <Button
                flex="1"
                leftIcon={<CollecticonXmarkSmall />}
                onClick={() => validate(selectedInference.properties.id, false)}
                isDisabled={isValidating}
                isLoading={isValidating}
                colorScheme="red"
                variant="outline"
              >
                Invalid
              </Button>
              <Button
                flex="1"
                leftIcon={<CollecticonTickSmall />}
                onClick={() => validate(selectedInference.properties.id, true)}
                isDisabled={isValidating}
                isLoading={isValidating}
                colorScheme="green"
                variant="outline"
              >
                Valid
              </Button>
            </ButtonGroup>
            {isValidating && (
              <Loading
                display="flex"
                gap="2"
                justifyContent="center"
                fontSize="sm"
                opacity="0.75"
              >
                Updating validation status...
              </Loading>
            )}
            {validationError && (
              <Error
                display="flex"
                gap="2"
                fontWeight="normal"
                justifyContent="center"
                iconSize={6}
              >
                {" "}
                {validationError}
              </Error>
            )}
            <Accordion allowToggle>
              <AccordionItem>
                <h4>
                  <AccordionButton>
                    <Box as="span" flex="1" fontWeight="bold" textAlign="left">
                      Image Controls
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h4>
                <AccordionPanel>
                  <ImageControls
                    brightness={brightness}
                    setBrightness={setBrightness}
                    contrast={contrast}
                    setContrast={setContrast}
                  />
                  <Checkbox
                    name="bbox-mask-checkbox"
                    size="sm"
                    isChecked={bboxMask}
                    mt={4}
                    fontWeight="semibold"
                    colorScheme="primary"
                    onChange={(e) => setBboxMask(e.target.checked)}
                  >
                    Show Mask
                  </Checkbox>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </PanelBody>
        </Panel>
      </Box>
    </Box>
  );
}

export default Validate;
